import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Card, Drawer, Flex, Grid, Skeleton, Space, Typography } from 'antd';
import type { RichText } from '@titularis/api-client';
import { api } from '../../api';
import { EditorTexto } from '../../components/EditorTexto';
import { origemDoComentario } from './origemDoComentario';
import {
  conteudoDaCaixa,
  rascunhoDoArtigo,
  type RascunhoDoComentario,
} from './rascunhoDoComentario';

/**
 * O comentário de um artigo, aberto para ler, corrigir e publicar.
 *
 * **Mora fora das telas que a abrem** porque são duas: o inventário, onde se
 * procura o comentário para corrigir, e a lei artigo a artigo, onde se chega
 * nele pelo texto da lei. Duas gavetas iguais divergiriam na primeira mudança - e
 * a que fica para trás é justamente a que publica.
 */

/** O bastante para abrir: o id manda, o resto é o título. */
export interface ArtigoNaGaveta {
  id: string;
  lei: string;
  artigo: string;
}

const useCompacto = () => !Grid.useBreakpoint().md;

export function GavetaDoComentario({
  artigo,
  onClose,
}: {
  artigo: ArtigoNaGaveta | null;
  onClose: () => void;
}) {
  const compacto = useCompacto();
  const { message } = App.useApp();
  const clienteDeConsultas = useQueryClient();
  const [rascunho, setRascunho] = useState<RascunhoDoComentario | null>(null);

  const detalhe = useQuery({
    queryKey: ['commented-article', artigo?.id],
    queryFn: () => api.getCommentedArticle(artigo!.id),
    enabled: artigo !== null,
  });

  /** Nulo enquanto o artigo aberto não foi tocado - e o Salvar, desligado. */
  const rascunhoDaqui = rascunhoDoArtigo(rascunho, artigo?.id);

  /**
   * Fechar é sempre por aqui, e não só pelo `onClose` da gaveta.
   *
   * Publicar fecha a gaveta de dentro da mutação, e era o caminho por onde o
   * rascunho escapava: o revisor publicava o artigo, abria o seguinte e
   * encontrava lá o comentário do anterior.
   */
  const fechar = () => {
    setRascunho(null);
    onClose();
  };

  /**
   * O que volta a valer depois de salvar ou publicar.
   *
   * As três listas que mostram o estado do artigo - o inventário, o backlog e a
   * lei artigo a artigo - saem de rotas diferentes, e deixar qualquer uma de
   * fora faria a linha continuar dizendo "A revisar" depois de publicada.
   */
  const invalidar = async () => {
    await Promise.all([
      clienteDeConsultas.invalidateQueries({ queryKey: ['commented-articles'] }),
      clienteDeConsultas.invalidateQueries({ queryKey: ['commented-article', artigo?.id] }),
      clienteDeConsultas.invalidateQueries({ queryKey: ['article-backlog'] }),
      clienteDeConsultas.invalidateQueries({ queryKey: ['law-articles'] }),
      clienteDeConsultas.invalidateQueries({ queryKey: ['admin-laws'] }),
    ]);
  };

  const salvar = useMutation({
    mutationFn: () => api.updateArticleCommentary(artigo!.id, rascunhoDaqui),
    onSuccess: async () => {
      message.success('Comentário salvo.');
      await invalidar();
    },
    // O portão da citação responde 400 com o motivo: mostrar a mensagem crua é
    // o certo, porque ela nomeia a citação que não bate com a fonte.
    onError: (erro: Error) => message.error(erro.message),
  });

  const publicar = useMutation({
    mutationFn: (publicado: boolean) => api.setArticleCommentaryPublication(artigo!.id, publicado),
    onSuccess: async (_dados, publicado) => {
      message.success(publicado ? 'Comentário no ar.' : 'Comentário fora do ar.');
      await invalidar();
      fechar();
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const conteudo = conteudoDaCaixa(
    rascunho,
    artigo?.id,
    detalhe.data?.content?.doc as RichText | undefined,
  );
  const publicado = detalhe.data?.state === 'publicado';

  /** De onde veio o texto que está aí embaixo, e o que ele custou (G-COST). */
  const origem = origemDoComentario(detalhe.data);

  const acoes = (
    <Space size="small">
      <Button
        size={compacto ? 'small' : undefined}
        loading={salvar.isPending}
        disabled={rascunhoDaqui === null}
        onClick={() => salvar.mutate()}
      >
        Salvar
      </Button>
      {/*
       * **Publicar é ato humano, e só existe aqui** (G-HUMAN-GATE). Nenhuma
       * ferramenta do MCP tem esse caminho, e é o que garante que nada escrito
       * por máquina chegue ao aluno sem revisão.
       */}
      <Button
        type="primary"
        size={compacto ? 'small' : undefined}
        danger={publicado}
        loading={publicar.isPending}
        disabled={!detalhe.data?.content}
        onClick={() => publicar.mutate(!publicado)}
      >
        {publicado ? 'Tirar do ar' : 'Publicar'}
      </Button>
    </Space>
  );

  return (
    <Drawer
      open={artigo !== null}
      /*
       * **Largura fixa não cabe no celular.** 720px numa tela de 390 deixa o
       * conteúdo pela metade e sem rolagem lateral - o botão de publicar fica
       * fora da tela. Cheia no compacto, e por baixo, que é de onde o polegar
       * alcança.
       *
       * `size` mede sempre o eixo em que a gaveta cresce: altura quando ela vem
       * de baixo, largura quando vem da direita. É a prop de hoje - `width` e
       * `height` estão depreciadas no Antd v6 e avisam no console.
       */
      placement={compacto ? 'bottom' : 'right'}
      size={compacto ? '92%' : 720}
      onClose={fechar}
      title={artigo ? `art. ${artigo.artigo} da ${artigo.lei}` : ''}
      // No celular as ações vão para o rodapé: no cabeçalho elas espremeriam o
      // título até ele virar reticências.
      extra={compacto ? undefined : acoes}
      footer={compacto ? <Flex justify="flex-end">{acoes}</Flex> : undefined}
    >
      {detalhe.isPending ? (
        <Skeleton active paragraph={{ rows: 10 }} />
      ) : (
        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
          {origem ? <Typography.Text type="secondary">{origem}</Typography.Text> : null}
          {detalhe.data?.briefing ? (
            <Alert
              type="info"
              showIcon
              title="Ressalva do revisor"
              description={detalhe.data.briefing}
            />
          ) : null}
          {detalhe.data?.engineNote ? (
            <Alert
              type="warning"
              showIcon
              title="Observação do motor"
              description={detalhe.data.engineNote}
            />
          ) : null}

          {/* O texto oficial ao lado do comentário: conferir sem abrir o Planalto. */}
          <Card size="small" title="O artigo, no acervo">
            <Space orientation="vertical" size={6} style={{ width: '100%' }}>
              {(detalhe.data?.dispositivos ?? []).map((dispositivo) => (
                <Typography.Paragraph key={dispositivo.address} style={{ margin: 0 }}>
                  {dispositivo.content}
                </Typography.Paragraph>
              ))}
            </Space>
          </Card>

          <Card size="small" title="Comentário">
            <EditorTexto
              value={conteudo}
              onChange={(doc) =>
                setRascunho(doc !== null && artigo !== null ? { artigoId: artigo.id, doc } : null)
              }
              minHeight={compacto ? 200 : 260}
              placeholder="O que este artigo exige que o estudante saiba."
            />
          </Card>
        </Space>
      )}
    </Drawer>
  );
}
